import React, { useEffect, useState } from 'react'
import { Heading, Text } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { modulesService } from '../services/modules'
import PortaisAtendimentoGestaoFilaTab from './PortaisAtendimentoGestaoFilaTab'
import PortaisAtendimentoFilaAutomaticaTab from './PortaisAtendimentoFilaAutomaticaTab'
import PortaisAtendimentoOutrosTab from './PortaisAtendimentoOutrosTab'
import PortaisAtendimentoFormulariosTab from './PortaisAtendimentoFormulariosTab'
import PortaisAtendimentoUserActivityLogsTab from './PortaisAtendimentoUserActivityLogsTab'

const MODULE_NAME = 'portais_atendimento'

type MainTabId = 'parametros' | 'formularios' | 'log_usuarios'
type ParametroSubTabId = 'manual' | 'automatica' | 'outros'

const PortaisAtendimento: React.FC = () => {
  const [activeMainTab, setActiveMainTab] = useState<MainTabId>('parametros')
  const [activeParametroSubTab, setActiveParametroSubTab] = useState<ParametroSubTabId>('manual')
  const [moduleTitle, setModuleTitle] = useState('Portal Atendimento')
  const [moduleDescription, setModuleDescription] = useState('')

  useEffect(() => {
    const loadModuleInfo = async () => {
      try {
        const result = await modulesService.getByName(MODULE_NAME)
        if (result.success && result.data) {
          if (result.data.display_name) {
            setModuleTitle(result.data.display_name)
          }
          if (result.data.description) {
            setModuleDescription(result.data.description)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar informações do módulo:', error)
      }
    }

    void loadModuleInfo()
  }, [])

  const mainTabButtonClass = (active: boolean) =>
    `py-4 px-1 border-b-2 font-medium text-sm ${
      active
        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
    }`

  const subTabButtonClass = (active: boolean) =>
    `py-3 px-3 border-b-2 font-medium text-sm rounded-t-md ${
      active
        ? 'border-primary-500 text-primary-600 dark:text-primary-400 bg-white dark:bg-gray-800'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
    }`

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4 min-w-0">
                <img
                  src="/Logo_portal_de_atendimento_PASI-03.png"
                  alt="Portal de Atendimento PASI"
                  className="h-14 w-auto flex-shrink-0 object-contain select-none"
                />
                <div className="min-w-0">
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                    {moduleTitle}
                  </Heading>
                  <Text size="sm" color="muted" className="mt-2">
                    {moduleDescription}
                  </Text>
                </div>
              </div>
            </div>

            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-8 px-6" aria-label="Abas principais">
                <button
                  type="button"
                  className={mainTabButtonClass(activeMainTab === 'parametros')}
                  onClick={() => setActiveMainTab('parametros')}
                >
                  Parâmetros
                </button>
                <button
                  type="button"
                  className={mainTabButtonClass(activeMainTab === 'formularios')}
                  onClick={() => setActiveMainTab('formularios')}
                >
                  Formulários
                </button>
                <button
                  type="button"
                  className={mainTabButtonClass(activeMainTab === 'log_usuarios')}
                  onClick={() => setActiveMainTab('log_usuarios')}
                >
                  Log de Usuários
                </button>
              </nav>
            </div>

            {activeMainTab === 'parametros' && (
              <>
                <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <nav className="flex space-x-2 px-6" aria-label="Subabas de parâmetros">
                    <button
                      type="button"
                      className={subTabButtonClass(activeParametroSubTab === 'manual')}
                      onClick={() => setActiveParametroSubTab('manual')}
                    >
                      Fila Manual
                    </button>
                    <button
                      type="button"
                      className={subTabButtonClass(activeParametroSubTab === 'automatica')}
                      onClick={() => setActiveParametroSubTab('automatica')}
                    >
                      Fila Automática
                    </button>
                    <button
                      type="button"
                      className={subTabButtonClass(activeParametroSubTab === 'outros')}
                      onClick={() => setActiveParametroSubTab('outros')}
                    >
                      Outros
                    </button>
                  </nav>
                </div>

                {activeParametroSubTab === 'manual' && <PortaisAtendimentoGestaoFilaTab />}
                {activeParametroSubTab === 'automatica' && <PortaisAtendimentoFilaAutomaticaTab />}
                {activeParametroSubTab === 'outros' && <PortaisAtendimentoOutrosTab />}
              </>
            )}

            {activeMainTab === 'formularios' && <PortaisAtendimentoFormulariosTab />}

            {activeMainTab === 'log_usuarios' && <PortaisAtendimentoUserActivityLogsTab />}
          </div>
        </PermissionGuard>
      </div>
  )
}

export default PortaisAtendimento
